import { Injectable } from '@angular/core'
import { Params } from '@angular/router'
import { Observable } from 'rxjs'
import { LogEvent } from 'src/app/interfaces/log'
import { TokenResponse } from 'src/app/interfaces/main'
import { LogService } from 'src/app/services/log/log.service'
import { QuizService } from 'src/app/services/quiz/quiz.service'
import { environment } from 'src/environments/environment'
import { GeneralService } from '../general/general.service'
import { CustomHttpClient } from '../http/http.service'
import { LocaleService } from '../locale/locale-service'
import { RouterService } from '../router/router.service'
import { SnackbarService } from '../snackbar/snackbar.service'
import { UserService } from '../user/user.service'

declare var google: any
declare var AppleID: any

@Injectable({
  providedIn: 'root',
})
export class SignupService {
  constructor(
    private http: CustomHttpClient,
    private routerService: RouterService,
    private userService: UserService,
    private quizService: QuizService,
    private logService: LogService,
    private snackbarService: SnackbarService,
    private localeService: LocaleService,
    private generalService: GeneralService
  ) {}

  // init 후 가야할 주소가 있을 경우 그곳으로 이동
  async init(
    pathToGo: string | null = null,
    flag?: 'signin' | 'signup' | 'kakaoFriendAgreed',
    rootPath: string = 'gnb/main'
  ): Promise<void> {
    const { isLoggedIn } = this.userService
    await this.initLoginUser(flag)
    // navigate with profile status
    this.routerService.navigate(rootPath, { replaceUrl: true })

    if (pathToGo) {
      const url: URL = new URL(pathToGo)
      const queryParams: Params = {}
      url.searchParams.forEach((data, key) => (queryParams[key] = data))
      const navigateTimeout = setTimeout(() => {
        this.routerService.navigate(url.pathname, { queryParams })
        clearTimeout(navigateTimeout)
      }, 300)
    }
  }

  private async initLoginUser(flag?: 'signin' | 'signup' | 'kakaoFriendAgreed'): Promise<void> {
    const { isLoggedIn } = this.userService
    this.logService.setSuperProperty('is_signed_in', isLoggedIn + '')
    try {
      if (flag === 'kakaoFriendAgreed') {
        this.userService.setKakaoFriendAgreed(true)
      } else {
        if (isLoggedIn) {
          await this.userService.getProfile().toPromise()
          flag === 'signup' && this.logService.eventLogging(LogEvent.SIGN_UP_SUCCESS)
          flag === 'signin' && this.logService.eventLogging(LogEvent.SIGN_IN_SUCCESS)
        }
        // 새로 로그인 한 유저의 경우 localStorage에 저장된 응답 기록들 저장시키기
        this.quizService.connectDataToLoginUser()
      }
    } catch (error) {
      console.error('ERROR: get user profile failed')
    }

    try {
      const res = await this.userService.getMyPrivateData().toPromise()
      this.localeService.setInitialLocale(isLoggedIn, res?.data?.languageCode)
    } catch (error) {
      console.error(error)
    }
  }

  getTokenUsingKakaoCode(code: string): Observable<TokenResponse> {
    return this.http.post(
      `${environment.apiUrl}/v1/oauth/kakao/web`,
      { code },
      { loggingText: 'get token after web kakao login' }
    )
  }

  getTokenUsingGoogle(code: string): Observable<TokenResponse> {
    const redirectUri = this.getRedirectUri('google')
    return this.http.post(`${environment.apiUrl}/v1/oauth/google`, { code, redirectUri })
  }

  getTokenUsingApple(code: string, identityToken: string): Observable<TokenResponse> {
    return this.http.post(`${environment.apiUrl}/v1/oauth/apple`, { code, identityToken, platform: 'web' })
  }

  loginWithApple(url?: string): void {
    const clientId = 'com.woot.types.signin-web'
    const scope = 'name email'
    const redirectURI = this.getRedirectUri('apple')
    const state = url ?? ''
    AppleID.auth.init({
      clientId,
      scope,
      redirectURI,
      state,
      usePopup: true,
    })
    AppleID.auth.signIn()
    document.addEventListener('AppleIDSignInOnSuccess', (data: any) => {
      const code = data?.detail?.authorization?.code
      const idToken = data?.detail?.authorization?.id_token
      if (code && idToken) {
        this.routerService.navigate(`oauth/apple`, {
          queryParams: {
            code,
            state,
            id_token: idToken,
          },
        })
      }
      document.removeEventListener('AppleIDSignInOnSuccess', () => {})
    })
  }

  loginWithGoogle(url?: string): void {
    if (/(kakaotalk|instagram|facebook)/.test(navigator.userAgent.toLowerCase())) {
      this.snackbarService.openSnackBar(`${this.localeService.getLocaleString('외부 브라우저를 사용해주세요')}.`)
      return
    }
    if (!google) {
      this.onGoogleLoginFailed()
      return
    }
    const clientId = '759544068391-3lbnr0t8ci5c0alr8cra8p6lj7kdt55t.apps.googleusercontent.com'
    const googleClient = google.accounts.oauth2.initCodeClient({
      client_id: clientId,
      scope: 'email profile',
      ux_mode: 'redirect',
      redirect_uri: this.getRedirectUri('google'),
      state: url ? encodeURIComponent(url) : undefined,
      error_callback: (error: any) => {
        this.onGoogleLoginFailed()
      },
    })
    if (googleClient) {
      googleClient.requestCode()
    } else {
      this.onGoogleLoginFailed()
    }
  }

  private onGoogleLoginFailed(): void {
    this.snackbarService.openSnackBar(`${this.localeService.getLocaleString('외부 브라우저를 사용해주세요')}.`)
  }

  private getRedirectUri(type: 'google' | 'apple'): string {
    return `https://${location.hostname}/oauth/${type}`
  }
}
